import { create } from 'zustand';

export interface PortalConfig {
  id?: string;
  tenantId?: string;
  portalName: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  headerColor?: string;
  footerColor?: string;
  welcomeMessage?: string;
  footerText?: string;
  customCss?: string;
  headerHtml?: string;
  footerHtml?: string;
  metaTitle?: string;
  metaDescription?: string;
  googleAnalyticsId?: string;
  enableSearch: boolean;
  enableChat: boolean;
  enableTicketSubmission: boolean;
  enableTicketTracking: boolean;
  enableFeedback: boolean;
  enableRatings: boolean;
  enableContactForm: boolean;
  enableDarkMode: boolean;
}

const DEFAULT_CONFIG: PortalConfig = {
  portalName: 'Help Center',
  primaryColor: '#4f46e5',
  enableSearch: true,
  enableChat: false,
  enableTicketSubmission: true,
  enableTicketTracking: true,
  enableFeedback: true,
  enableRatings: true,
  enableContactForm: true,
  enableDarkMode: false,
};

interface PortalConfigState {
  config: PortalConfig;
  isLoaded: boolean;
  isDarkMode: boolean;
  fetchConfig: () => Promise<void>;
  toggleDarkMode: () => void;
}

export const usePortalConfigStore = create<PortalConfigState>()((set, get) => ({
  config: DEFAULT_CONFIG,
  isLoaded: false,
  isDarkMode: false,

  toggleDarkMode: () => {
    const { config, isDarkMode } = get();
    if (!config.enableDarkMode) return;

    const newValue = !isDarkMode;
    set({ isDarkMode: newValue });

    if (newValue) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    localStorage.setItem('portal-dark-mode', newValue ? 'true' : 'false');
  },

  fetchConfig: async () => {
    try {
      const response = await fetch('/api/v1/portal/config', {
        headers: {
          'x-tenant-id': '11111111-1111-1111-1111-111111111111',
        },
      });

      if (!response.ok) {
        set({ isLoaded: true });
        return;
      }

      const data = await response.json();
      const config: PortalConfig = {
        ...DEFAULT_CONFIG,
        ...data,
        enableSearch: data.enableSearch ?? true,
        enableChat: data.enableChat ?? false,
        enableTicketSubmission: data.enableTicketSubmission ?? true,
        enableTicketTracking: data.enableTicketTracking ?? true,
        enableFeedback: data.enableFeedback ?? true,
        enableRatings: data.enableRatings ?? true,
        enableContactForm: data.enableContactForm ?? true,
        enableDarkMode: data.enableDarkMode ?? false,
      };

      // Restore dark mode preference from localStorage (only if admin allows it)
      let isDarkMode = false;
      if (config.enableDarkMode) {
        const stored = localStorage.getItem('portal-dark-mode');
        if (stored === 'true') {
          isDarkMode = true;
          document.documentElement.classList.add('dark');
        }
      } else {
        // Admin disabled dark mode — force light
        document.documentElement.classList.remove('dark');
        localStorage.removeItem('portal-dark-mode');
      }

      set({ config, isLoaded: true, isDarkMode });

      // Apply CSS variables to document root
      applyTheme(config);

      // Apply favicon if provided
      if (config.faviconUrl) {
        applyFavicon(config.faviconUrl);
      }

      // Apply document title
      if (config.metaTitle || config.portalName) {
        document.title = config.metaTitle || config.portalName;
      }

      // Apply meta description
      if (config.metaDescription) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.setAttribute('name', 'description');
          document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', config.metaDescription);
      }

      // Inject custom CSS if provided
      if (config.customCss) {
        injectCustomCss(config.customCss);
      }

      // Inject Google Analytics if ID is provided
      if (config.googleAnalyticsId) {
        injectGoogleAnalytics(config.googleAnalyticsId);
      }
    } catch {
      set({ isLoaded: true });
    }
  },
}));

function applyTheme(config: PortalConfig) {
  const root = document.documentElement;

  if (config.primaryColor) {
    root.style.setProperty('--portal-primary', config.primaryColor);
    root.style.setProperty('--portal-primary-light', adjustColor(config.primaryColor, 40));
    root.style.setProperty('--portal-primary-dark', adjustColor(config.primaryColor, -20));
  }
  if (config.secondaryColor) {
    root.style.setProperty('--portal-secondary', config.secondaryColor);
  }
  if (config.accentColor) {
    root.style.setProperty('--portal-accent', config.accentColor);
  }
  if (config.backgroundColor) {
    root.style.setProperty('--portal-bg', config.backgroundColor);
  }
  if (config.textColor) {
    root.style.setProperty('--portal-text', config.textColor);
  }
  if (config.headerColor) {
    root.style.setProperty('--portal-header', config.headerColor);
  }
  if (config.footerColor) {
    root.style.setProperty('--portal-footer', config.footerColor);
  }
}

function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function applyFavicon(url: string) {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function injectCustomCss(css: string) {
  const existingStyle = document.getElementById('portal-custom-css');
  if (existingStyle) {
    existingStyle.textContent = css;
    return;
  }
  const style = document.createElement('style');
  style.id = 'portal-custom-css';
  style.textContent = css;
  document.head.appendChild(style);
}

function injectGoogleAnalytics(gaId: string) {
  // Don't inject twice
  if (document.getElementById('portal-ga-script')) return;

  // Google Analytics gtag.js script
  const script = document.createElement('script');
  script.id = 'portal-ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  // Initialize gtag
  const initScript = document.createElement('script');
  initScript.id = 'portal-ga-init';
  initScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}');
  `;
  document.head.appendChild(initScript);
}
